<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { getCompanyCreditNotes, type CreditNote } from '@/api/commercialDashboardApi'
import { formatDate } from './dashboardFormat'

const props = defineProps<{
  company: { uuid: string; name: string } | null
}>()

const emit = defineEmits<{ close: [] }>()

const creditNotes = ref<CreditNote[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

const total = computed(() => creditNotes.value.reduce((sum, inv) => sum + (inv.amountResidual ?? 0), 0))

watch(
  () => props.company,
  async (company) => {
    if (!company) return
    loading.value = true
    error.value = null
    creditNotes.value = []
    try {
      creditNotes.value = await getCompanyCreditNotes(company.uuid)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des avoirs'
    } finally {
      loading.value = false
    }
  }
)

const preciseAmountFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
function formatAmountPrecise(amount: number | null): string {
  return preciseAmountFormatter.format(amount ?? 0)
}

const PAYMENT_STATE_LABELS: Record<string, string> = {
  not_paid: 'Disponible',
  partial: 'Partiellement utilisé',
  in_payment: "En cours d'utilisation",
  reversed: 'Extourné',
}
function paymentStateLabel(state: string | null): string {
  if (!state) return '—'
  return PAYMENT_STATE_LABELS[state] ?? state
}
</script>

<template>
  <div v-if="company" class="modal modal-open">
    <div class="modal-box max-w-3xl flex flex-col max-h-[80vh] p-0">
      <!-- En-tête épinglé -->
      <div class="flex items-start justify-between gap-4 shrink-0 p-5 pb-3 border-b border-base-200">
        <div>
          <h3 class="text-lg font-bold">Avoirs disponibles</h3>
          <p class="text-sm opacity-60">
            {{ company.name }}
            <span v-if="!loading && creditNotes.length > 0">· {{ creditNotes.length }} avoir{{ creditNotes.length > 1 ? 's' : '' }}</span>
          </p>
        </div>
        <button class="btn btn-sm btn-circle btn-ghost" @click="emit('close')">✕</button>
      </div>

      <div v-if="loading" class="flex justify-center py-10">
        <span class="loading loading-spinner loading-lg text-primary"></span>
      </div>

      <div v-else-if="error" class="alert alert-error m-5">
        <span class="text-sm">{{ error }}</span>
      </div>

      <div v-else-if="creditNotes.length === 0" class="py-10 text-center text-sm opacity-60">
        Aucun avoir disponible.
      </div>

      <!-- Zone défilante : seule la liste scrolle -->
      <div v-else class="flex-1 overflow-auto px-5">
        <table class="table table-sm">
          <thead class="sticky top-0 bg-base-100 z-10">
            <tr>
              <th>Avoir</th>
              <th>Date</th>
              <th class="text-right">Total</th>
              <th class="text-right">Disponible</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="inv in creditNotes" :key="inv.uuid">
              <td class="font-medium">{{ inv.name ?? '—' }}</td>
              <td class="text-sm whitespace-nowrap">{{ inv.invoiceDate ? formatDate(inv.invoiceDate) : '—' }}</td>
              <td class="text-right text-sm whitespace-nowrap">{{ formatAmountPrecise(inv.amountTotal) }}</td>
              <td class="text-right text-sm font-semibold text-blue-600 whitespace-nowrap">{{ formatAmountPrecise(inv.amountResidual) }}</td>
              <td class="text-xs">{{ paymentStateLabel(inv.paymentState) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pied épinglé : total + fermer -->
      <div class="flex items-center justify-between gap-4 shrink-0 p-5 pt-3 border-t border-base-200">
        <div class="text-sm">
          <span class="opacity-60">Total avoirs disponibles&nbsp;:</span>
          <span class="ml-1 font-bold text-blue-600">{{ formatAmountPrecise(total) }}</span>
        </div>
        <button class="btn btn-sm" @click="emit('close')">Fermer</button>
      </div>
    </div>
    <div class="modal-backdrop bg-black/40" @click="emit('close')"></div>
  </div>
</template>
